import React, { useEffect, useState, useMemo } from 'react'
import CommonTablePage, { CommonTablePagePropsInterface } from '../../../components/common/common-table/common-table-page'
// import moment from 'moment'
import moment from 'moment-timezone'
import { DatePicker, TimePicker, Input, Select, Button, Space, Form, message } from 'antd'
import * as XLSX from 'xlsx'
import { getLeaveRequestsByDate } from './utils/leave'

const { RangePicker } = DatePicker

const getThisWeek = () => {
  const monday = moment().startOf('isoWeek')
  const sunday = monday.clone().add(6, 'days')
  return {
    startStr: monday.format('YYYY-MM-DD'),
    endStr: sunday.format('YYYY-MM-DD'),
    startMoment: monday,
    endMoment: sunday

  }
}

const basePostUrl = 'attendance/weekly-summary'

function buildUtcIsoFromLocalDateAndTime(baseDate: any, hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number)
  const dateOnly = moment(baseDate).format('YYYY-MM-DD')
  const local = moment(`${dateOnly} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`, 'YYYY-MM-DD HH:mm:ss')
  return local.utc().toISOString()
}

const WeeklyAttendancePage: React.FC = () => {
  const [triggerResetData, setTriggerResetData] = useState(false)
  const [url, setUrl] = useState<string>(basePostUrl)

  const [employeeName, setEmployeeName] = useState<string>()
  // const [department, setDepartment] = useState<string>()
  // const [position, setPosition] = useState<string>()
  const [attendanceGroup, setAttendanceGroup] = useState<string>()
  const [dateRange, setDateRange] = useState<[any, any] | null>([
    getThisWeek().startMoment,
    getThisWeek().endMoment,
  ])
  const [checkInTime, setCheckInTime] = useState<any>(null)
  const [leaveData, setLeaveData] = useState<any[]>([])
  const [exportRows, setExportRows] = useState<any[]>([])
  const [loadingParams, setLoadingParams] = useState<any>(null)

  const [restRequestOptions, setRestRequestOptions] = useState<any>(() => {
    const { startStr, endStr } = getThisWeek()
    return {
      method: 'post',
      data: {
        EmployeeName: null,
        // Department: null,
        // Position: null,
        attendanceGroup: null,
        DateStartUtc: moment(startStr, 'YYYY-MM-DD').utc().toISOString(),
        DateEndUtc: moment(endStr, 'YYYY-MM-DD').utc().toISOString(),
        CheckIntUtc: null,
      },
    }
  })

  const loadLeaves = async (startIso: string, endIso: string) => {
    try {
      const data = await getLeaveRequestsByDate(startIso, endIso)
      setLeaveData(data)
    } catch (e) {
      console.error(e)
      message.error('Failed to load leave data')
      setLeaveData([])
    }
  }

  // 初始加载
  useEffect(() => {
    const { startStr, endStr } = getThisWeek()
    const startIso = moment(startStr, 'YYYY-MM-DD').utc().toISOString()
    const endIso = moment(endStr, 'YYYY-MM-DD').utc().toISOString()

    const nextBody = {
      EmployeeName: null,
      attendanceGroup: null,
      DateStartUtc: startIso,
      DateEndUtc: endIso,
      CheckIntUtc: null,
    }

    // 先加载假期数据，完成后再触发表格刷新
    loadLeaves(startIso, endIso).then(() => {
      setRestRequestOptions({ method: 'post', data: nextBody })
      setUrl(basePostUrl)
      setTriggerResetData(t => !t)
    })
  }, [])

  // 点击 Apply 时触发
  const applyWithParams = () => {
    const startIso = (dateRange?.[0]
      ? moment(dateRange[0]).utc()
      : moment(getThisWeek().startStr, 'YYYY-MM-DD').utc()
    ).toISOString()

    const endIso = (dateRange?.[1]
      ? moment(dateRange[1]).utc()
      : moment(getThisWeek().endStr, 'YYYY-MM-DD').utc()
    ).toISOString()

    const nextBody = {
      EmployeeName: employeeName?.trim() || null,
      // Department: department || null,
      // Position: position || null,
      attendanceGroup: attendanceGroup || null,
      DateStartUtc: startIso,
      DateEndUtc: endIso,
      CheckIntUtc: checkInTime
        ? buildUtcIsoFromLocalDateAndTime(dateRange?.[0] || new Date(), moment(checkInTime).format('HH:mm'))
        : null,
    }

    // 先加载假期数据，完成后再触发表格刷新
    loadLeaves(startIso, endIso).then(() => {
      setRestRequestOptions({ method: 'post', data: nextBody })
      setUrl(basePostUrl)
      setTriggerResetData(t => !t)
    })
  }

  // 当 loadingParams 改变时，同时触发两个数据加载
  useEffect(() => {
    if (!loadingParams) return

    // 同步更新所有状态，确保两个 API 在同一时刻触发
    setRestRequestOptions({ method: 'post', data: loadingParams.data })
    setUrl(basePostUrl)
    setTriggerResetData(t => !t)   // 触发 CommonTablePage 重新请求
    void loadLeaves(loadingParams.startIso, loadingParams.endIso)
  }, [loadingParams])

  const mappingRenderData = (rows: any[]) => {
    const mappedAttendance = (rows || []).map((r) => {
      const inLocal = r.checkInTimeUtc ? moment.utc(r.checkInTimeUtc).tz('Pacific/Auckland') : null
      const outLocal = r.checkOutTimeUtc ? moment.utc(r.checkOutTimeUtc).tz('Pacific/Auckland') : null
      const daily =
        typeof r.workHours === 'number'
          ? r.workHours.toFixed(2)
          : (inLocal && outLocal && outLocal.isAfter(inLocal))
            ? (outLocal.diff(inLocal, 'minutes') / 60).toFixed(2)
            : ''

      return {
        ...r,
        employeeName: r.employeeName ?? '',
        // department: r.department ?? '',
        // position: r.position ?? '',
        attendanceGroup: r.attendanceGroup ?? '',
        dateOnly: r.date ?? (inLocal ? inLocal.format('YYYY-MM-DD') : ''),
        checkInLocal: inLocal ? inLocal.format('HH:mm') : '',
        checkOutLocal: outLocal ? outLocal.format('HH:mm') : '',
        dailyHours: daily,
      }
    })
    // 2) 再把 leaveData 映射成“按天”行（append，不会覆盖 records）
    const mappedLeaves: any[] = []
      ; (leaveData || []).forEach((lv: any) => {
        const start = moment(lv.startDate).tz('Pacific/Auckland').startOf('day')
        const end = moment(lv.endDate).tz('Pacific/Auckland').startOf('day')

        const name = `${lv?.employee?.firstName ?? ''} ${lv?.employee?.lastName ?? ''}`.trim()
        const ag = lv?.employee?.attendanceDept ?? ''          // 你要的 AG
        const leaveTypeName = lv?.leaveType?.typeName ?? ''    // check-in
        const approvalStatus = lv?.approvalStatus ?? ''        // check-out

        for (let d = start.clone(); d.isSameOrBefore(end, 'day'); d.add(1, 'day')) {
          const dateOnly = d.format('YYYY-MM-DD')
          mappedLeaves.push({
            __rowType: 'leave',
            __leaveRequestId: lv.leaveRequestId,
            employeeName: name,
            attendanceGroup: ag,
            dateOnly,
            checkInLocal: leaveTypeName,
            checkOutLocal: approvalStatus,
            dailyHours: '0.00',
          })
        }
      })

    const merged = [...mappedAttendance, ...mappedLeaves].sort((a, b) => {
      const d = String(a.dateOnly || '').localeCompare(String(b.dateOnly || ''))
      if (d !== 0) return d
      return String(a.employeeName || '').localeCompare(String(b.employeeName || ''))
    })

    setExportRows(merged)
    return merged
  }

  const columns = [
    { title: 'Name', field: 'employeeName' },
    { title: 'Attendance Group', field: 'attendanceGroup' },
    { title: 'Date', field: 'dateOnly' },
    { title: 'Check-in', field: 'checkInLocal' },
    { title: 'Check-out', field: 'checkOutLocal' },
    { title: 'Working Hour', field: 'dailyHours' },
  ]

  const tableProps: Partial<CommonTablePagePropsInterface> = {
    title: 'Weekly Attendance',
    column: columns as any,
    isNotAddable: true,
    isNotEditable: true,
    isNotDeletable: true,
    getAllUrl: url,
    restRequestOptions,
    mappingRenderData,
    triggerResetData,
  }

  const handleExportTable = () => {
    if (!exportRows || exportRows.length === 0) {
      message.info('There are currently no exportable data.')
      return
    }

    // 顶部两行：筛选条件
    const startStr = dateRange?.[0] ? moment(dateRange[0]).format('YYYY-MM-DD') : getThisWeek().startStr
    const endStr = dateRange?.[1] ? moment(dateRange[1]).format('YYYY-MM-DD') : getThisWeek().endStr

    const headerLines = [
      [`DateStart(NZ): ${moment(startStr, 'YYYY-MM-DD').local().format('YYYY-MM-DD HH:mm')}`,
      `DateEnd(NZ): ${moment(endStr, 'YYYY-MM-DD').local().format('YYYY-MM-DD HH:mm')}`,
      `Employee: ${employeeName || '-'}`],
      [`AttendanceGroup: ${attendanceGroup || '-'}`,
      `Exported(NZ): ${new Date().toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland' })}`],
      [] // 空行
    ]

    // 列头
    const heads = ['Name', 'Attendance Group', 'Date', 'Check-in', 'Check-out', 'Working Hour']

    // 数据（使用表格显示过的字段，已是本地时间字符串）
    const body = exportRows.map(r => [
      r.employeeName, r.attendanceGroup, r.dateOnly, r.checkInLocal, r.checkOutLocal, r.dailyHours
    ])

    // ===== 新增：员工总时长统计 =====
    const totalsMap: Record<string, number> = {}
    exportRows.forEach(r => {
      const hrs = parseFloat(r.dailyHours || '0')
      if (!isNaN(hrs)) {
        totalsMap[r.employeeName] = (totalsMap[r.employeeName] || 0) + hrs
      }
    })

    body.push([]) // 空行
    body.push(['Employee Totals Working Hours']) // 标题
    body.push(['Name', 'Total Hours']) // 表头
    Object.entries(totalsMap).forEach(([name, total]) => {
      body.push([name, total.toFixed(2)])
    })

    const allRows = [...headerLines, heads, ...body]
    const ws = XLSX.utils.aoa_to_sheet(allRows)

    // 自动筛选
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerLines.length, c: 0 },
        e: { r: headerLines.length + Math.max(body.length, 1), c: heads.length - 1 }
      })
    }
    // 冻结到表头
    ws['!freeze'] = { xSplit: 0, ySplit: headerLines.length + 1 }

    // 自适应列宽（简单实现）
    const colCount = heads.length
    const widths = new Array(colCount).fill(10)
    allRows.forEach(row => {
      row.forEach((cell, i) => {
        const s = cell == null ? '' : String(cell)
        widths[i] = Math.max(widths[i], s.length + 2)
      })
    })
    ws['!cols'] = widths.map(w => ({ wch: Math.min(Math.max(w, 8), 40) }))

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Summary')

    const fname = `Summary_${startStr}_to_${endStr}.xlsx`
    XLSX.writeFile(wb, fname)
  }

  // 周区间：按 ISO 周（Mon–Sun）生成头部 "DD/MM/YYYY-DD/MM/YYYY"
  const handleExportWeekly = () => {
    if (!exportRows || exportRows.length === 0) {
      message.info('There are currently no exportable data.')
      return
    }

    // ---- 工具：求周日与周六（不依赖 locale） ----
    const mondayStart = (m: moment.Moment) => m.clone().startOf('isoWeek')
    const sundayEnd = (m: moment.Moment) => m.clone().endOf('isoWeek')

    // ---- 计算“打卡周期”：以数据里的最小/最大日期，外扩到周日-周六 ----
    const allDates = (exportRows || [])
      .map((r: any) => r.dateOnly || r.date ||
        ((r.checkInTimeUtc || r.checkOutTimeUtc)
          ? moment.utc(r.checkInTimeUtc || r.checkOutTimeUtc).local().format('YYYY-MM-DD')
          : null))
      .filter(Boolean)
      .map(s => moment(s as string, 'YYYY-MM-DD'))

    let displayLabel = ''
    let fileLabel = ''
    if (allDates.length) {
      const minD = moment.min(allDates)
      const maxD = moment.max(allDates)
      const headStart = mondayStart(minD)
      const headEnd = sundayEnd(maxD)
      displayLabel = `${headStart.format('DD/MM/YYYY')}-${headEnd.format('DD/MM/YYYY')}`
      fileLabel = `${headStart.format('DD_MM_YYYY')}-${headEnd.format('DD_MM_YYYY')}`
    } else {
      // 兜底：用当前选择的范围
      const start = dateRange?.[0] ? moment(dateRange[0]) : moment()
      const end = dateRange?.[1] ? moment(dateRange[1]) : moment()
      displayLabel = `${mondayStart(start).format('DD/MM/YYYY')}-${sundayEnd(end).format('DD/MM/YYYY')}`
      fileLabel = `${mondayStart(start).format('DD_MM_YYYY')}-${sundayEnd(end).format('DD_MM_YYYY')}`
    }

    // ---- 以“Attendance Group -> Employee”分组，并确定输出顺序 ----
    const groupMap: Record<string, Record<string, any[]>> = (exportRows || []).reduce((acc, r: any) => {
      const g = (r.attendanceGroup || '').trim() || 'Unknown'
      const name = (r.employeeName || '').trim() || 'Unknown'
      acc[g] = acc[g] || {}
      acc[g][name] = acc[g][name] || []
      acc[g][name].push(r)
      return acc
    }, {})

    //是否显示 Week 行：仅当“导出周期”跨越两周时才显示（以 periodLabel 判定）
    const periodStartMon = mondayStart(moment(displayLabel.split('-')[0].trim(), 'DD/MM/YYYY'))
    const periodEndMon = mondayStart(moment(displayLabel.split('-')[1].trim(), 'DD/MM/YYYY'))
    const showWeekHeader = periodEndMon.format('YYYY-MM-DD') !== periodStartMon.format('YYYY-MM-DD')

    // Group 顺序：字母升序（Unknown 放最后）
    const groupKeys = Object.keys(groupMap).sort((a, b) => {
      const A = a === 'Unknown', B = b === 'Unknown'
      if (A && !B) return 1
      if (!A && B) return -1
      return a.localeCompare(b, undefined, { sensitivity: 'base' })
    })

    // ---- AOA & 合并信息 ----
    const aoa: any[][] = []
    const merges: Array<{ s: { r: number, c: number }; e: { r: number, c: number } }> = []

    // 顶部行：打卡周期
    aoa.push(['打卡周期', displayLabel], [])


    // ---- 按组 -> 按姓名输出（姓名升序）----
    groupKeys.forEach((groupKey, groupIdx) => {
      const byEmp = groupMap[groupKey]
      const empNames = Object.keys(byEmp).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      )

      if (groupIdx > 0) aoa.push([], []) // 不同组之间空两行
      empNames.forEach((empName, empIdx) => {
        const rows = byEmp[empName] || []
        // 1) 先把该员工的记录按“周起=周日”分组
        const weeksMap: Record<string, any[]> = {}
        rows.forEach(r => {
          const dateKeyStr =
            r.dateOnly || r.date ||
            ((r.checkInTimeUtc || r.checkOutTimeUtc)
              ? moment.utc(r.checkInTimeUtc || r.checkOutTimeUtc).local().format('YYYY-MM-DD')
              : null)
          if (!dateKeyStr) return
          const m = moment(dateKeyStr, 'YYYY-MM-DD', true)
          if (!m.isValid()) return
          const wkMon = mondayStart(m).format('YYYY-MM-DD')
          if (!weeksMap[wkMon]) weeksMap[wkMon] = []
          weeksMap[wkMon].push(r)
        })

        const weekKeys = Object.keys(weeksMap).sort() // 升序输出

        weekKeys.forEach((wkKey, wkIdx) => {
          const wkMon = moment(wkKey, 'YYYY-MM-DD')
          const wkSun = wkMon.clone().add(6, 'day')
          if (showWeekHeader) {
            if (wkIdx > 0) aoa.push([])
            aoa.push([`Week: ${wkMon.format('YYYY-MM-DD')} to ${wkSun.format('YYYY-MM-DD')}`])
          }

          // 本周 7 天：周日→周六
          const daysThisWeek = Array.from({ length: 7 }).map((_, i) => {
            const d = wkMon.clone().add(i, 'day') // Sun + i -> ... -> Sat
            return { label: d.format('ddd'), key: d.format('YYYY-MM-DD'), date: d }
          })

          // 区块之间空两行（不同员工或同员工不同周，都空行）
          if (empIdx > 0 || wkIdx > 0) aoa.push([], [])

          // 区块抬头
          aoa.push(['Attendance group', groupKey, 'Employee Name', empName])
          aoa.push(['Working day', 'Check in time', 'Check out time', 'Working hours'])

          // 2) 本周 dayMap：用本地时分文本（checkInLocal/checkOutLocal）
          const dayMap: Record<string, { inLocal?: string; outLocal?: string; work?: number; }> = {}
          weeksMap[wkKey].forEach(r => {
            const k = r.dateOnly || r.date ||
              ((r.checkInTimeUtc || r.checkOutTimeUtc)
                ? moment.utc(r.checkInTimeUtc || r.checkOutTimeUtc).local().format('YYYY-MM-DD')
                : '')
            if (!k) return
            if (!dayMap[k]) dayMap[k] = {}
            if (r.checkInLocal) dayMap[k].inLocal = String(r.checkInLocal)   // "HH:mm"
            if (r.checkOutLocal) dayMap[k].outLocal = String(r.checkOutLocal)  // "HH:mm"
            if (r.__rowType === 'leave') dayMap[k].work = 0
            else if (typeof r.workHours === 'number') dayMap[k].work = r.workHours
            // if (r.isPaidLeave) { dayMap[k].isLeave = true; dayMap[k].leaveText = r.leaveType || 'Paid sick leave/annual leave' }
          })

          // 3) 输出本周 7 天 + 汇总
          const detailStartRow = aoa.length + 1

          let actualTotal = 0   // 本周实际工时合计
          let paidLeaveTotal = 0 // 本周带薪假合计

          daysThisWeek.forEach(d => {
            const rec = dayMap[d.key] || {}

            // if (rec.isLeave) {
            //   const rowIndex1 = aoa.length + 1
            //   aoa.push([d.label, rec.leaveText || 'Paid sick leave/annual leave', null, null])
            //   merges.push({ s: { r: rowIndex1 - 1, c: 1 }, e: { r: rowIndex1 - 1, c: 3 } })
            //   // paidLeaveTotal += 8
            //   return
            // }

            const inText = rec.inLocal || ''
            const outText = rec.outLocal || ''

            // Excel formula for working hours: =IF(AND(Bx<>"",Cx<>""), (TIMEVALUE(Cx)-TIMEVALUE(Bx))*24, "")
            // Bx: check in, Cx: check out
            let hours: number | string | null = null
            if (typeof rec.work === 'number') {
              hours = Number(rec.work.toFixed(2))
            } else if (inText && outText) {
              hours = null // will be replaced by formula below
            }

            // Prepare row
            aoa.push([d.label, inText, outText, hours])

            // If no backend workHours, set formula for Excel
            if ((rec.work === undefined || rec.work === null) && inText && outText) {
              // Row index in Excel is aoa.length (1-based), but we just pushed, so it's aoa.length
              const excelRow = aoa.length
              // D column is index 3, B is 1, C is 2
              // Formula: =IF(AND(Bx<>"",Cx<>""),(TIMEVALUE(Cx)-TIMEVALUE(Bx))*24,"")
              // e.g. D5: =IF(AND(B5<>"",C5<>""),(TIMEVALUE(C5)-TIMEVALUE(B5))*24,"")
              if (!aoa[excelRow - 1][3]) aoa[excelRow - 1][3] = { f: `IF(AND(B${excelRow}<>"",C${excelRow}<>""),(TIMEVALUE(C${excelRow})-TIMEVALUE(B${excelRow}))*24,"")` }
            }

            // For actualTotal, use backend workHours if available, else will be calculated in Excel
            if (typeof rec.work === 'number') actualTotal += rec.work
          })

          // Summary rows with formulas
          const firstDetailRow = detailStartRow
          const lastDetailRow = aoa.length

          // Actual Working Hours: sum D column for this week
          aoa.push([null, null, 'Actual Working Hours', { f: `SUM(D${firstDetailRow}:D${lastDetailRow})` }])
          aoa.push([null, null, 'Paid Leave Hours', Number(paidLeaveTotal.toFixed(2))])
          aoa.push([null, null, 'Total Paid Hours', { f: `D${aoa.length}+D${aoa.length - 1}` }])
        })
      })
    })

    // ---- 转 Sheet & 样式 ----
    const ws = XLSX.utils.aoa_to_sheet(aoa)
    ws['!cols'] = [
      { wch: 13.1 }, // A Working day
      { wch: 12.4 }, // B Check in
      { wch: 19.8 }, // C Check out
      { wch: 15.2 }, // D Working hours
    ]
    if (merges.length) {
      // @ts-ignore
      ws['!merges'] = (ws['!merges'] || []).concat(merges)
    }
    Object.keys(ws).forEach(addr => {
      if (/^D\d+$/.test(addr)) {
        const cell: any = ws[addr]
        if (cell && (typeof cell.v === 'number' || typeof cell.f === 'string')) {
          cell.z = '0.00_);[Red]\\(0.00\\)'
        }
      }
    })

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')

    const fname = `Weekly__${fileLabel}.xlsx`
    XLSX.writeFile(wb, fname)
  }

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Form layout="inline" onFinish={() => applyWithParams()} >
          <Form.Item label="Date Range">
            <RangePicker
              value={dateRange as any}
              onChange={(vals) => setDateRange(vals as any)}
              format="YYYY/MM/DD"
            />
          </Form.Item>

          <Form.Item label="Name">
            <Input
              value={employeeName}
              onChange={(e) => setEmployeeName(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
          </Form.Item>

          <Form.Item label="Attendance Group">
            <Select
              value={attendanceGroup}
              onChange={setAttendanceGroup}
              allowClear
              style={{ width: 160 }}
              options={[
                { label: 'Office', value: 'Office' },
                { label: 'Production', value: 'Production' },
                { label: 'Warehouse', value: 'Warehouse' },
                { label: 'Printing', value: 'Printing' },
                { label: 'AttendanceFree', value: 'AttendanceFree' },
              ]}
            />
          </Form.Item>

          <Form.Item label="Check-in Time">
            <TimePicker
              value={checkInTime}
              onChange={setCheckInTime}
              format="HH:mm"
              minuteStep={5}
              allowClear
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ margin: 10 }}>
              <Button type="primary" htmlType="submit">Apply</Button>
              <Button onClick={handleExportWeekly}>Download Weekly records</Button>
              <Button onClick={handleExportTable}>Download Table</Button>
            </Space>
          </Form.Item>
        </Form>

        <CommonTablePage {...tableProps} />
      </Space>
    </div>
  )
}
export default WeeklyAttendancePage